/**
 * Accessibility enhancements
 * ---------------------------------------------------------------------------
 * Added September 2026 to bring the product page in line with WCAG 2.1 AA.
 *
 * It fixes three things that blocked keyboard and screen-reader shoppers:
 *
 *   1. The "Select Size" drawer opened, but keyboard focus stayed on the button
 *      behind it, so the size options were unreachable.
 *   2. The size cells are <div> elements driven by click handlers in custom.js.
 *      Clicks come only from a mouse, so a keyboard could never choose a size.
 *      This file makes them real buttons and makes Enter and Space activate them.
 *   3. The gallery images and thumbnails are links with no text, so a screen
 *      reader announced only a filename. This gives each one a real name.
 *
 * Deliberately dependency-free and defensive: if any element is missing it
 * does nothing rather than throwing, so it can never break the page.
 */
(function () {
  'use strict';

  var FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled])',
    'select:not([disabled])', 'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])', '[role="button"]:not([aria-disabled="true"])'
  ].join(',');

  function visibleFocusables(root) {
    if (!root) return [];
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0;
    });
  }

  // -------------------------------------------------------------------------
  // 1 & 2. The size drawer
  // -------------------------------------------------------------------------
  function initSizeDrawer() {
    var drawer = document.querySelector('.form-sidebar');
    var trigger = document.querySelector('.variant-picker-trigger');
    if (!drawer) return;

    var titleEl = drawer.querySelector('.sidebar-title');
    if (titleEl && !titleEl.id) titleEl.id = 'size-drawer-title';

    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    if (titleEl) drawer.setAttribute('aria-labelledby', titleEl.id);

    if (trigger) {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', 'false');
      if (!drawer.id) drawer.id = 'size-drawer';
      trigger.setAttribute('aria-controls', drawer.id);
    }

    // Every size cell becomes a proper toggle button for assistive tech.
    function upgradeCells() {
      var cells = drawer.querySelectorAll('.option-cell.has-value');
      Array.prototype.forEach.call(cells, function (cell) {
        if (cell.getAttribute('data-a11y-ready')) return;
        cell.setAttribute('data-a11y-ready', 'true');
        cell.setAttribute('role', 'button');
        cell.setAttribute('tabindex', '0');
        cell.setAttribute('aria-pressed', cell.classList.contains('selected') ? 'true' : 'false');

        var value = cell.getAttribute('data-value') || '';
        if (!cell.getAttribute('aria-label') && value) {
          cell.setAttribute('aria-label', 'Size ' + value.replace(/\(slim\)/i, 'slim fit'));
        }
      });
    }

    // Keyboard users press Enter or Space; custom.js only listens for clicks.
    drawer.addEventListener('keydown', function (evt) {
      var cell = evt.target.closest && evt.target.closest('.option-cell.has-value');
      if (!cell) return;
      if (evt.key !== 'Enter' && evt.key !== ' ' && evt.key !== 'Spacebar') return;
      evt.preventDefault();
      cell.click();
    });

    // Keep the pressed state in sync with whatever the theme marks as selected.
    drawer.addEventListener('click', function (evt) {
      if (!evt.target.closest || !evt.target.closest('.option-cell.has-value')) return;
      window.setTimeout(function () {
        Array.prototype.forEach.call(
          drawer.querySelectorAll('.option-cell.has-value'),
          function (c) { c.setAttribute('aria-pressed', c.classList.contains('selected') ? 'true' : 'false'); }
        );
      }, 0);
    });

    function isOpen() { return drawer.classList.contains('expanded'); }

    function closeDrawer() {
      var closer = drawer.querySelector('.close-sidebar') || drawer.querySelector('.sidebar-overlay');
      if (closer) {
        closer.click();
      } else {
        drawer.classList.remove('expanded');
        document.body.classList.remove('overflow-hidden');
      }
    }

    function onOpen() {
      upgradeCells();
      if (trigger) trigger.setAttribute('aria-expanded', 'true');

      // Move focus into the drawer so the size options are actually reachable.
      var target = drawer.querySelector('.close-sidebar') ||
                   drawer.querySelector('.option-cell.has-value') ||
                   visibleFocusables(drawer)[0];
      if (target) {
        window.setTimeout(function () { target.focus(); }, 60);
      }
    }

    function onClose() {
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.focus();
      }
    }

    // custom.js toggles the "expanded" class, so watch that rather than
    // duplicating its click handling.
    var wasOpen = isOpen();
    new MutationObserver(function () {
      var open = isOpen();
      if (open === wasOpen) return;
      wasOpen = open;
      if (open) { onOpen(); } else { onClose(); }
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });

    // Escape closes; Tab stays inside while the drawer is open.
    document.addEventListener('keydown', function (evt) {
      if (!isOpen()) return;

      if (evt.key === 'Escape' || evt.key === 'Esc') {
        evt.preventDefault();
        closeDrawer();
        return;
      }

      if (evt.key !== 'Tab') return;
      var items = visibleFocusables(drawer);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];

      if (evt.shiftKey && document.activeElement === first) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && document.activeElement === last) {
        evt.preventDefault();
        first.focus();
      } else if (!drawer.contains(document.activeElement)) {
        evt.preventDefault();
        first.focus();
      }
    });

    upgradeCells();
  }

  // -------------------------------------------------------------------------
  // 3. Image links that announced only a filename
  // -------------------------------------------------------------------------
  function initImageLabels() {
    var titleEl = document.querySelector('.product-title');
    var productName = titleEl ? titleEl.textContent.trim() : 'product';

    function label(link, text) {
      if (!link) return;
      var img = link.querySelector('img');
      var hasAlt = img && (img.getAttribute('alt') || '').trim();
      if (hasAlt || (link.getAttribute('aria-label') || '').trim()) return;
      if (img) img.setAttribute('alt', text);
      link.setAttribute('aria-label', text);
    }

    var galleryLinks = document.querySelectorAll('a.show-gallery');
    Array.prototype.forEach.call(galleryLinks, function (link, i) {
      label(link, productName + ' — view larger image ' + (i + 1) + ' of ' + galleryLinks.length);
    });

    var thumbs = document.querySelectorAll('a.thumbnail');
    Array.prototype.forEach.call(thumbs, function (link, i) {
      var isVideo = link.classList.contains('thumbnail--media-video');
      label(link, productName + (isVideo ? ' — play video' : ' — show image ' + (i + 1)));
    });
  }

  function init() {
    try { initSizeDrawer(); } catch (e) { /* never break the page */ }
    try { initImageLabels(); } catch (e) { /* never break the page */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());
